<template>
  <div class="mx-5 px-5">
    <div class="table-responsive">
      <table class="table table-about mx-auto">
        <tbody>
          <tr v-for="module in modules" :key="module.label">
            <td class="text-center">
              <span :id="module.label + '-status'"
                    :class="module.status === 'OK' ? 'on' : (module.status === 'KO' ? 'off' : 'unknown')"
                    class="switch"
              />
            </td>
            <td class="font-weight-bold"><span class="font-weight-bold">{{ module.label }}</span></td>
            <td>{{ module.version }}</td>
            <td>{{ module.date | date }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="!cannotStart && hasLicense && isRole.superAdmin" class="text-center mt-3">
      <p class="font-weight-bold">{{ $t('aboutVersions:license') | capitalize }}</p>
      <p class="license-container">
        <span>{{ $t('aboutVersions:expirationDate') | capitalize }}:</span>
        <span>{{ application.license.expirationDate || '∞' | date }}</span>
      </p>
      <p class="license-container">
        <span>{{ $t('aboutVersions:maxUsers') | capitalize }}:</span>
        <span>{{ application.license.maxUsers || '∞' }}</span>
      </p>
      <p class="license-container">
        <span>{{ $t('aboutVersions:maxGateways') | capitalize }}:</span>
        <span>{{ application.license.maxGateways || '∞' }}</span>
      </p>
      <p class="license-container">
        <span>{{ $t('aboutVersions:maxEndDevices') | capitalize }}:</span>
        <span>{{ application.license.maxEndDevices || '∞' }}</span>
      </p>
    </div>

    <div v-if="!cannotStart && tokenExpirationDate" class="text-center mt-5">
      {{ $t('aboutVersions:tokenExpirationDate') | capitalize }} {{ tokenExpirationDate | date }}
    </div>

    <div class="text-center mt-4">
      &copy; {{ $t('aboutVersions:copyright') | capitalize }}<br>
      <a id="emailSupportLink" :href="'mailto:'+supportEmail">{{ $t('aboutVersions:contact') | capitalize }}</a>
    </div>
  </div>
</template>

<script>
import { sortBy, filter, find } from 'lodash-es'
import { mapGetters } from 'vuex'
import to from '@/modules/await-to-js'
import constants from '@/configuration/constants'
import gatewayBrand from '@/filters/gatewayBrand'

const labels = {
  'BSC': 'Base Station Controller',
  'LNS': 'LoRa Network Server',
  'RNC': 'Radio Network Controller',
  'WMS': 'Workflow Management Service',
  'WG': 'Wanesy Geolocation'
}

export default {
  props: {
    // When displayed in cannot start error page: do not show expiration date
    cannotStart: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      showMetrics: false,
      titleMetrics: '',
      remoteShell: {}
    }
  },

  computed: {
    // mix the getters into computed with object spread operator
    ...mapGetters(['application']),

    supportEmail () {
      return constants.supportEmail
    },

    modules () {
      const modules = [
        {
          label: 'Dashboard',
          status: 'OK',
          version: _VERSION,
          date: _BUILD_DATE
        }, {
          label: 'Remote Shell Server',
          ...this.remoteShell
        }, {
          label: 'Gateway Management Service',
          version: this.application.version,
          date: this.application.date,
          status: this.application.status
        }
      ]

      sortBy(this.application.components, 'name').forEach(component => {
        let label = component.name
        if (labels[component.name]) label = labels[component.name]
        if (label.indexOf('BSC') !== -1) label = gatewayBrand(label)

        modules.push({ label, ...this.getComponent(component.name) })
      })

      // Remove NOT_CONFIGURED modules
      return filter(modules, m => m.status !== 'NOT_CONFIGURED')
    },

    tokenExpirationDate () {
      return this.$api.getTokenExpirationDate()
    },

    hasLicense () {
      return this.application.license && (
        this.application.license.expirationDate ||
        this.application.license.maxUsers ||
        this.application.license.maxGateways ||
        this.application.license.maxEndDevices)
    }
  },

  mounted () {
    if (!this.cannotStart) {
      this.$store.dispatch('getApplication')
    }

    this.getRemoteShell()
  },

  methods: {
    async getRemoteShell () {
      let error, response
      [error, response] = await to(this.$api.getRemoteShellVersion())

      if (error || !response) {
        this.remoteShell = { status: 'KO' }
        return
      }

      this.remoteShell = {
        status: 'OK',
        date: response.data.date,
        version: response.data.version
      }
    },

    getComponent (componentName, subcomponentName) {
      const component = find(this.application.components, c => c.name === componentName) || { status: 'NOT_CONFIGURED' }
      const subcomponent = find(component.components, c => c.name === subcomponentName) || { status: 'NOT_CONFIGURED' }

      const cpnt = subcomponentName ? subcomponent : component
      return {
        version: cpnt.version,
        date: cpnt.date,
        status: cpnt.status
      }
    }
  }
}
</script>

<style scoped lang="scss">
  $maxContainerWidth: 550px;

  .table-about {
    max-width: $maxContainerWidth;

    th,
    td {
      vertical-align: middle;
      border: none;
    }
  }

  .license-container {
    display: flex;
    justify-content: space-between;
    max-width: calc(#{$maxContainerWidth} / 2);
    // text-align: left;
    margin: 5px auto;
  }
</style>
